import { Group, MultiSelect, Paper, Select, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '../../../components';
import { useJobs } from '../../../queries/use-jobs';
import { useTableState } from '../../../hooks/use-table-state';
import { PAGE_SIZE } from '../../../magic-values';
import { JobsTable } from './jobs-table';
import { JobStatus, JobTrigger, type Job } from '../../../types/job';
import type { JobsFilters } from '../../../api/job-service';

const JOB_STATUSES = Object.values(JobStatus);
const JOB_TRIGGERS = Object.values(JobTrigger);
// Known job phases; the type filter is exact-match on the backend, so unknown
// phases can still be entered via the creatable option.
const JOB_TYPES = [
  'ingestion',
  'processing',
  'llm_processing',
  'aggregation',
  'data_out',
  'onboarding',
  'stats',
  'admin',
];

interface JobsTabProps {
  accountId: string;
}

export function JobsTab({ accountId }: JobsTabProps) {
  const navigate = useNavigate();
  const {
    page,
    sortBy,
    sortDir,
    sortStatus,
    filterBy,
    setPage,
    setSortStatus,
    setFilterBy,
    getFilter,
    setFilter,
  } = useTableState();
  const status = getFilter('status');
  const type = getFilter('type');
  const trigger = getFilter('trigger');

  const filters: JobsFilters = { accountId };
  if (filterBy) filters.filterBy = filterBy;
  if (status) filters.status = status;
  if (type) filters.type = type;
  if (trigger) filters.trigger = trigger;

  const { data, isLoading, isError, error } = useJobs(filters, {
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
  });
  const jobs = data?.items ?? [];

  const typeOptions = type && !JOB_TYPES.includes(type) ? [...JOB_TYPES, type] : JOB_TYPES;

  const handleRowClick = (job: Job) => {
    navigate(`/account/${accountId}/job/${job._id}`);
  };

  return (
    <Stack spacing="sm">
      <Group position="apart" align="flex-start">
        <Group spacing="sm">
          <MultiSelect
            data={JOB_STATUSES}
            value={status ? status.split(',') : []}
            onChange={(values) => setFilter('status', values.join(','))}
            placeholder="Status"
            clearable
            sx={{ minWidth: 220 }}
          />
          <Select
            data={typeOptions}
            value={type || null}
            onChange={(value) => setFilter('type', value ?? '')}
            placeholder="Type"
            clearable
            searchable
            creatable
            getCreateLabel={(query) => `Filter: ${query}`}
            onCreate={(query) => {
              setFilter('type', query);
              return query;
            }}
            sx={{ width: 180 }}
          />
          <Select
            data={JOB_TRIGGERS}
            value={trigger || null}
            onChange={(value) => setFilter('trigger', value ?? '')}
            placeholder="Trigger"
            clearable
            sx={{ width: 160 }}
          />
        </Group>
        <SearchInput
          value={filterBy}
          onChange={setFilterBy}
          placeholder="Search by origin or id…"
        />
      </Group>
      {isError ? (
        <Text color="red">Failed to load jobs: {(error as Error)?.message ?? 'Unknown error'}</Text>
      ) : (
        <Paper withBorder sx={{ overflowX: 'auto' }}>
          <JobsTable
            jobs={jobs}
            fetching={isLoading}
            onRowClick={handleRowClick}
            page={page}
            totalRecords={data?.total ?? 0}
            onPageChange={setPage}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            height="calc(100vh - 500px)"
          />
        </Paper>
      )}
    </Stack>
  );
}
